import { defineStore } from 'pinia';
import { ChangelogService } from '@/services/changelog';

/** State of the changelog module */
export interface ChangelogState {
  /** Loaded changelog entries (each with e.g. `createdAt`; possible keys: intent) */
  changelogs: any[];
  /** Whether the related content has been saved (used to flag pending changelog writes) */
  isContentSaved: boolean;
  /** Whether changelogs are currently being fetched */
  loading: boolean;
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/changelog.ts` (namespace
 * `changelog`, registered via the re-export in `src/store/modules/index.ts`). In-memory only —
 * no `vuexPersist` option (this namespace had no reducer entry in `src/store/index.ts`).
 *
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - Legacy had BOTH a state prop `changelogs` and a getter `changelogs` that was a pure identity
 *   passthrough (`(state) => state.changelogs`). Per the state/getter collision rule, the getter
 *   is renamed `changelogsGetter`; consumers reading `store.changelogs` now read state directly.
 * - The generic legacy mutation `set({key, value})` becomes a same-named action `set`; it is kept
 *   because it's used as a shared setter internally (by `setSavedStatus` and `fetchChangelogs`)
 *   and may also be invoked directly by consumers via `commit('changelog/set', ...)` today.
 */
export const useChangelogStore = defineStore('changelog', {
  state: (): ChangelogState => ({
    changelogs: [], // possible keys: intent
    isContentSaved: false,
    loading: false,
  }),
  getters: {
    /**
     * Renamed from legacy getter `changelogs` (collides with the state prop of the same name —
     * the state prop wins the name). Pure identity passthrough.
     */
    changelogsGetter(): any[] {
      return this.changelogs;
    },
    /**
     * Returns the changelog list sorted by `createdAt` descending.
     *
     * NOTE: `Array.prototype.sort` mutates in place, exactly as the legacy getter did — behavior
     * intentionally preserved as-is.
     */
    changelogListSorted(): any[] {
      if (!this.changelogs) return [];

      return this.changelogs.sort(
        (a, b) => (new Date(b.createdAt) as any) - (new Date(a.createdAt) as any),
      );
    },
  },
  actions: {
    /**
     * Converted from the legacy generic mutation `set({key, value})`. Kept as a generic setter
     * for parity with existing `commit('changelog/set', ...)` call sites.
     */
    set(payload: { key: string; value: any }): void {
      (this as any)[payload.key] = payload.value;
    },
    /**
     * Set the saved status in the changelog store.
     *
     * @param isSaved - The flag to indicate whether content is saved or not.
     */
    setSavedStatus(isSaved: boolean): void {
      this.set({ key: 'isContentSaved', value: isSaved });
    },
    /**
     * Save the changelog.
     * @param changelog
     * @returns {Promise<ChangelogEntity[]|{error: *}>}
     */
    save(changelog: any) {
      return ChangelogService.createChangelog(changelog);
    },
    async fetchChangelogs(params: any) {
      const { changelogType, ...rest } = params;
      const changelogs = await ChangelogService.searchChangelogByParams(rest);
      this.set({ key: 'changelogs', value: changelogs || [] });
      return this.changelogs;
    },
  },
});
